// Install a suite: pick a manifest (suites/index.ts), name it, and it is made
// as one undoable group: the Workspace (with `spec.suite`) and its own Home in
// Settings `phones[slug]`. Then the device switches to it. The same manifest
// can be installed again under another name.
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { mutate } from '@/runtime'
import { useWorkspaces } from '@/db/hooks'
import { KindError } from '@/kinds/kind'
import { Check } from 'lucide-react'
import { CORE_NAV } from '@/lib/nav'
import { appIdsFor } from '@/lib/useNav'
import { packsFor } from '@/packs'
import { savePhoneFor } from '@/os/phone'
import { useScope } from '@/state/scope'
import { installSpec, navToStore, SUITES, uniqueSlug, type SuiteManifest } from '@/suites'
import { Button, Field, Sheet, cx } from './ui'

/** Install `m` as `name`: the Workspace and its Home, one group for undo. Returns the new slug. */
export const installSuite = async (m: SuiteManifest, name: string, taken: string[], order: number) => {
  const slug = uniqueSlug(name, taken, m.id)
  const hidden = packsFor(m.packs).flatMap((p) => p.layout?.hides ?? [])
  const nav = navToStore(
    m,
    CORE_NAV.map((n) => n.key),
    hidden,
  )
  await mutate.grouped(async () => {
    await mutate.create('Workspace', installSpec(m, name, slug, { order, nav }))
    await savePhoneFor(slug, m.home(appIdsFor({ nav, packs: m.packs })))
  })
  return slug
}

/** `title`, or "title 2", "title 3"… when a suite is already called that. */
export const freshName = (title: string, names: string[]): string => {
  const have = new Set(names.map((n) => n.trim().toLowerCase()))
  if (!have.has(title.toLowerCase())) return title
  for (let n = 2; ; n++) if (!have.has(`${title} ${n}`.toLowerCase())) return `${title} ${n}`
}

export const SuiteInstallSheet = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const workspaces = useWorkspaces()
  const setWs = useScope((s) => s.setWs)
  const navigate = useNavigate()
  const [pick, setPick] = useState<SuiteManifest>(SUITES[0]!)
  // the name follows the chosen manifest's title until it is typed in
  const [typed, setTyped] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // a fresh name by default: the manifest's title, numbered when a suite already has it ("Life 2")
  const name =
    typed ??
    freshName(
      pick.title,
      workspaces.map((w) => w.spec.name),
    )
  const taken = workspaces.map((w) => w.spec.slug)
  const close = () => {
    setTyped(null)
    setError(null)
    onClose()
  }
  const install = async () => {
    setBusy(true)
    setError(null)
    try {
      const order = Math.max(0, ...workspaces.map((w) => w.spec.order)) + 1
      const made = await installSuite(pick, name, taken, order)
      setWs(made)
      close()
      void navigate({ to: '/' })
    } catch (e) {
      setError(e instanceof KindError ? e.message : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open={open}
      onClose={close}
      title="Install a suite"
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void install()}
            disabled={busy}
            data-testid="suite-install"
          >
            Install
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted mb-3">
        A suite is its own place: its own things, its own apps and its own Home screen. Install the same one
        twice for two separate places.
      </p>
      <div className="flex flex-col gap-2 mb-4" role="radiogroup" aria-label="Suite">
        {SUITES.map((m) => {
          const on = m.id === pick.id
          return (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setPick(m)}
              className={cx(
                'card flex items-center gap-3 px-3 py-2.5 min-h-14 text-left transition-colors',
                on ? 'border-accent bg-accent-soft' : 'hover:border-line-strong',
              )}
              data-testid={`suite-${m.id}`}
            >
              <span className="text-2xl w-9 text-center" aria-hidden>
                {m.icon}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-medium">{m.title}</span>
                <span className="block text-xs text-faint">{m.blurb}</span>
              </span>
              {/* the chosen one is ticked, as a choice is everywhere else */}
              <span
                className={cx(
                  'h-6 w-6 rounded-full border shrink-0 flex items-center justify-center',
                  on ? 'bg-accent text-on-accent border-transparent' : 'border-line-strong',
                )}
                aria-hidden
              >
                {on && <Check size={14} strokeWidth={2.6} />}
              </span>
            </button>
          )
        })}
      </div>
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setTyped(e.target.value)}
          maxLength={60}
          className="w-full"
          data-testid="suite-name"
        />
      </Field>
      {error && <p className="text-sm text-rose-300 mb-3">{error}</p>}
    </Sheet>
  )
}
